import { Tooltip } from "antd";
import { Link } from "react-router-dom";

const text = (...values) => values.map((value) => String(value ?? "").trim()).find(Boolean) || "";
const nameFor = (resident = {}) => text(
  resident.name,
  resident.fullName,
  resident.residentName,
  [resident.firstName, resident.middleName, resident.lastName].filter(Boolean).join(" "),
  "Unnamed resident",
);
const initialsFor = (value = "") => value.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join("").toUpperCase() || "--";
const dateFor = (value) => {
  if (!value) return "Not assigned";
  const normalized = /^\d{4}-\d{2}-\d{2}$/.test(String(value)) ? `${value}T12:00:00` : value;
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? "Not assigned" : date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};
const clean = (value, fallback = "Not added") => {
  const source = Array.isArray(value) ? value.join(", ") : value;
  return String(source ?? "").replace(/<[^>]*>/g, " ").replace(/&nbsp;/gi, " ").replace(/&amp;/gi, "&").replace(/\s+/g, " ").trim() || fallback;
};
const photoUrlFor = (resident = {}) => {
  const value = resident.profilePhotoSignedUrl || resident.profilePhotoUrl;
  if (!value) return "";
  if (typeof value === "object") return text(value.signedUrl);
  const source = String(value).trim();
  if (!source.startsWith("{")) return source;
  try {
    return text(JSON.parse(source).signedUrl);
  } catch {
    return "";
  }
};

export default function ResidentInformationSummary({ resident = {}, items = [], className = "" }) {
  const residentName = nameFor(resident);
  const residentId = text(resident.id, resident.residentId, resident.uuid);
  const personal = resident.personalInformation || {};
  const residency = resident.residencyDetails || resident.location || {};
  const snapshot = resident.careSnapshot || resident.initialCareSnapshot || resident.onboardingCareSnapshot || {};
  const race = text(personal.race, resident.race);
  const raceOther = text(personal.raceOther, resident.raceOther);
  const heightFeet = personal.heightFeet ?? resident.heightFeet;
  const heightInches = personal.heightInches ?? resident.heightInches;
  const weight = personal.weightLbs ?? resident.weightLbs;
  const careTeam = Array.isArray(resident.careTeam) ? resident.careTeam : [];
  const pharmacies = Array.isArray(resident.pharmacies) ? resident.pharmacies : [];
  const profilePhotoUrl = photoUrlFor(resident);
  const defaultItems = [
    {
      label: "Residency",
      entries: [
        ["Bed", text(resident.bedNumber, resident.bed, residency.bedNumber, residency.bed, "Not assigned")],
        ["Room", text(resident.roomNumber, resident.room, residency.roomNumber, residency.room, "Not assigned")],
        ["Floor", text(resident.floorNumber, resident.floor, residency.floorNumber, residency.floor, "Not assigned")],
        ["Since", dateFor(text(resident.moveInDate, resident.movedInDate, resident.admissionDate, residency.moveInDate, residency.admissionDate))],
      ],
    },
    {
      label: "Demographics",
      entries: [
        ["Race", race.toLowerCase() === "other" && raceOther ? `Other: ${raceOther}` : race || "Not added"],
        ["Height", heightFeet !== undefined && heightFeet !== null && heightFeet !== "" ? `${heightFeet} ft${heightInches !== undefined && heightInches !== null && heightInches !== "" ? ` ${heightInches} in` : ""}` : "Not added"],
        ["Weight", weight !== undefined && weight !== null && weight !== "" ? `${weight} lbs` : "Not added"],
        ["Language", text(personal.primaryLanguage, resident.primaryLanguage, resident.language, "Not added")],
      ],
    },
    { label: "Allergies", value: text(resident.allergies, snapshot.allergies) },
    { label: "Diet", value: text(resident.diet, resident.dietaryNeeds, snapshot.diet) },
    { label: "Medication notes", value: text(resident.medicationNotes, snapshot.medicationNotes) },
    { label: "Current vital signs", value: text(resident.currentVitalSigns, resident.latestVitalSigns, "No current readings") },
    {
      label: "Care network",
      entries: [
        ["Provider", text(resident.primaryCareProviderName, resident.providerName, "Not added")],
        ["Care team", careTeam.map((member) => text(member.name, member.fullName)).filter(Boolean).join(", ") || "Not added"],
        ["Pharmacies", pharmacies.map((pharmacy) => text(pharmacy.name, pharmacy.pharmacyName)).filter(Boolean).join(", ") || "Not added"],
      ],
    },
  ];
  const displayItems = items.length ? items : defaultItems;
  const renderValue = (label, value) => {
    const fullValue = clean(value);
    return (
      <Tooltip title={fullValue} placement="bottom" zIndex={2200}>
        <strong className={`resident-information-summary-clamped${fullValue.length > 90 ? " is-long" : ""}`} aria-label={label}>{fullValue}</strong>
      </Tooltip>
    );
  };

  return (
    <section className={`resident-information-summary ${className}`.trim()} aria-label={`${residentName} resident information`}>
      <div className="medication-med-log-resident">
        <span className="medication-med-log-resident-photo">
          {profilePhotoUrl
            ? <img alt={`${residentName} profile`} src={profilePhotoUrl} />
            : initialsFor(residentName)}
        </span>
        <span className="medication-med-log-resident-copy">
          <span className="resident-information-summary-name">
            <strong>{residentName}</strong>
            {residentId ? (
              <Tooltip title="View resident details" placement="bottom" zIndex={2200}>
                <Link to={`/residents/${encodeURIComponent(residentId)}`} aria-label={`View ${residentName} details`}>
                  <i className="ti ti-eye" aria-hidden="true" />
                </Link>
              </Tooltip>
            ) : null}
          </span>
          <span className="medication-med-log-resident-facts">
            <span><b>Res. No.</b>{resident.residentNumber || "Not assigned"}</span>
            <span><b>DoB</b>{dateFor(resident.birthDate || resident.dateOfBirth || resident.dob)}</span>
            <span><b>Gender</b>{text(resident.sex, resident.gender, personal.sex, "Not added")}</span>
          </span>
        </span>
      </div>
      <div
        className="medication-admin-resident-summary"
        style={{ "--resident-summary-columns": displayItems.length }}
      >
        {displayItems.map((item) => (
          <span className={`medication-admin-resident-summary-item${item.entries ? " is-grouped" : ""}`} key={item.label}>
            <small>{item.label}</small>
            {item.entries ? (
              <span className="medication-admin-resident-summary-group">
                {item.entries.map(([entryLabel, entryValue]) => {
                  const fullValue = clean(entryValue);
                  return (
                    <span key={entryLabel}>
                      <b>{entryLabel}</b>
                      <Tooltip title={fullValue} placement="bottom" zIndex={2200}>
                        <em className={`resident-information-summary-clamped${fullValue.length > 22 ? " is-long" : ""}`}>{fullValue}</em>
                      </Tooltip>
                    </span>
                  );
                })}
              </span>
            ) : renderValue(item.label, item.value)}
          </span>
        ))}
      </div>
    </section>
  );
}
